import { useEffect, useMemo, useState, useRef } from 'react';
import { useLocation, Outlet } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

// material-ui
import { styled, useTheme, ThemeProvider } from '@mui/material/styles';
import { AppBar, Box, Container, CssBaseline, Toolbar } from '@mui/material';

// project imports
import Header from './Header';
import Footer from './Footer';

import useScreenSize from 'hooks/useScreenSize';

// assets
const homePageDesktopBgImg = `${process.env.REACT_APP_BASE_URL}/img/static/background/Home.webp`;
const homePageMobileBgImg = `${process.env.REACT_APP_BASE_URL}/img/static/background/Home-m.webp`;
const mediaServicePageDesktopBgImg = `${process.env.REACT_APP_BASE_URL}/img/static/background/Media-Service.webp`;
const mediaServicePageMobileBgImg = `${process.env.REACT_APP_BASE_URL}/img/static/background/Media-Service-m.webp`;
const dataDrivenProgrammaticMobileBgImg = `${process.env.REACT_APP_BASE_URL}/img/static/background/data-driven-programmatic-m.webp`;
const premiumPlacementBgImg = `${process.env.REACT_APP_BASE_URL}/img/static/background/premium-placement-2.webp`;
const premiumPlacementMobileBgImg = `${process.env.REACT_APP_BASE_URL}/img/static/background/premium-placement-m.webp`;
const mediaPlanningManagementMobileBgImg = `${process.env.REACT_APP_BASE_URL}/img/static/background/media-planning-management-m.webp`;
const chinaMarketSolutionsMobileBgImg = `${process.env.REACT_APP_BASE_URL}/img/static/background/china-market-solutions-m.webp`;
const aiCreationMobileBgImg = `${process.env.REACT_APP_BASE_URL}/img/static/background/ai-creation-m.webp`;
const dataServicePageDesktopBgImg = `${process.env.REACT_APP_BASE_URL}/img/static/background/data-service.webp`;
const dataServicePageMobileBgImg = `${process.env.REACT_APP_BASE_URL}/img/static/background/data-service-m.webp`;

function getMainLayoutDesktopBackgroundUrl(pathname) {
    if (pathname === '/') {
        return homePageDesktopBgImg;
    }
    if (
        pathname === '/media-service' ||
        pathname === '/data-driven-programmatic' ||
        pathname === '/campaign-management-service' ||
        pathname === '/china-media-solutions' ||
        pathname === '/ai-creation'
    ) {
        return mediaServicePageDesktopBgImg;
    }
    if (pathname === '/premium-placement') {
        return premiumPlacementBgImg;
    }
    if (pathname === '/data-service' || pathname === '/external-data' || pathname === '/insight-report' || pathname === '/etl-project') {
        return dataServicePageDesktopBgImg;
    }
    return null;
}

function getMainLayoutMobileBackgroundUrl(pathname) {
    if (pathname === '/') {
        return homePageMobileBgImg;
    }
    if (pathname === '/media-service') {
        return mediaServicePageMobileBgImg;
    }
    if (pathname === '/data-driven-programmatic') {
        return dataDrivenProgrammaticMobileBgImg;
    }
    if (pathname === '/premium-placement') {
        return premiumPlacementMobileBgImg;
    }
    if (pathname === '/campaign-management-service') {
        return mediaPlanningManagementMobileBgImg;
    }
    if (pathname === '/china-media-solutions') {
        return chinaMarketSolutionsMobileBgImg;
    }
    if (pathname === '/ai-creation') {
        return aiCreationMobileBgImg;
    }
    if (pathname === '/data-service' || pathname === '/external-data' || pathname === '/insight-report' || pathname === '/etl-project') {
        return dataServicePageMobileBgImg;
    }
    return null;
}

/** CSS background-image for viewports below md vs md+ (theme breakpoints; SSR-safe, no client-only media hook). */
function getMainLayoutBackgroundImageSx(desktopUrl, mobileUrl) {
    const belowMd = mobileUrl != null ? `url(${mobileUrl})` : desktopUrl != null ? `url(${desktopUrl})` : 'none';
    const mdUp = desktopUrl != null ? `url(${desktopUrl})` : mobileUrl != null ? `url(${mobileUrl})` : 'none';

    return {
        xs: belowMd,
        md: mdUp
    };
}

// styles
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(({ theme }) => ({
    ...theme.typography.mainContent
}));

// ==============================|| TESTING LAYOUT ||============================== //

const TestingLayout = () => {
    const theme = useTheme();
    const location = useLocation();
    const screenSize = useScreenSize();

    const [showBgColor, setShowBgColor] = useState(false);
    const [isVisible, setIsVisible] = useState(true);
    const lastScrollYRef = useRef(0);
    const touchStartY = useRef(0);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const onScroll = () => {
            const scroll = window.scrollY;
            const shouldShowBgColor = scroll > screenSize.height;
            setShowBgColor((prev) => (prev === shouldShowBgColor ? prev : shouldShowBgColor));
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [screenSize.height]);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const handleScroll = () => {
            const currentScrollY = window.scrollY;
            if (currentScrollY > lastScrollYRef.current && currentScrollY > 50) {
                // Scrolling down
                setIsVisible(false);
            } else if (currentScrollY < lastScrollYRef.current) {
                // Scrolling up
                setIsVisible(true);
            }
            lastScrollYRef.current = currentScrollY;
        };

        const handleTouchStart = (e) => {
            touchStartY.current = e.touches[0].clientY;
        };

        const handleTouchMove = (e) => {
            const currentTouchY = e.touches[0].clientY;
            const deltaY = touchStartY.current - currentTouchY;
            const currentScrollY = window.scrollY;

            if (Math.abs(deltaY) > 30) {
                if (deltaY > 0 && currentScrollY > 80) {
                    // Scrolling down
                    setIsVisible(false);
                } else if (deltaY < 0) {
                    // Scrolling up
                    setIsVisible(true);
                }
                lastScrollYRef.current = currentScrollY;
                touchStartY.current = currentTouchY;
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('touchstart', handleTouchStart, { passive: true });
        window.addEventListener('touchmove', handleTouchMove, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('touchstart', handleTouchStart);
            window.removeEventListener('touchmove', handleTouchMove);
        };
    }, []);

    const header = useMemo(
        () => (
            <Container
                sx={(t) => ({
                    backdropFilter: 'blur(5px)',
                    [t.breakpoints.up(900)]: {
                        maxWidth: 'unset'
                    },
                    [t.breakpoints.down(900)]: {
                        padding: '0 !important'
                    }
                })}
            >
                <Toolbar
                    sx={{
                        padding: '0 !important',
                        maxWidth: '1280px',
                        margin: '0 auto'
                    }}
                >
                    <Header />
                </Toolbar>
            </Container>
        ),
        []
    );

    const desktopBgUrl = getMainLayoutDesktopBackgroundUrl(location.pathname);
    const mobileBgUrl = getMainLayoutMobileBackgroundUrl(location.pathname);

    const mainBgDownMdMedia = theme.breakpoints.down('md').replace(/^@media\s*/, '');
    const mainBgUpMdMedia = theme.breakpoints.up('md').replace(/^@media\s*/, '');

    return (
        <Box sx={{ display: 'flex', flexFlow: 'column', position: 'relative', overflowX: 'hidden' }}>
            <Helmet>
                {desktopBgUrl ? <link rel="preload" as="image" href={desktopBgUrl} media={mainBgUpMdMedia} fetchPriority="low" /> : null}
                {mobileBgUrl ? <link rel="preload" as="image" href={mobileBgUrl} media={mainBgDownMdMedia} fetchPriority="low" /> : null}
            </Helmet>
            <CssBaseline />
            {/* header */}
            <AppBar
                enableColorOnDark
                position="fixed"
                color="transparent"
                elevation={0}
                sx={(t) => ({
                    bgcolor: showBgColor ? '#FFFFFFCC !important' : 'transparent !important',
                    [t.breakpoints.down(900)]: {
                        transition: 'opacity 0.5s ease-in-out',
                        transform: isVisible ? 'translateY(0)' : 'translateY(-100%)'
                    }
                })}
            >
                {header}
            </AppBar>

            {/* main content */}
            <Main
                sx={{
                    bgcolor: 'unset !important',
                    backgroundImage: getMainLayoutBackgroundImageSx(desktopBgUrl, mobileBgUrl),
                    backgroundSize: '100%',
                    backgroundRepeat: 'no-repeat',
                    marginRight: 0,
                    padding: 0
                }}
            >
                {/* breadcrumb */}
                <Container sx={{ maxWidth: 'unset !important', padding: '0 !important' }}>
                    <ThemeProvider theme={theme}>
                        <Outlet />
                    </ThemeProvider>
                </Container>
            </Main>
            <Footer />
        </Box>
    );
};

export default TestingLayout;
